import { getRequiredYarns, type RGProduct } from "@raumagarn/formatters";
import { YARN_TO_RECIPE_RATIO } from "~/config";
import type { CartHook } from "~/lib/hooks/useCart";

const getRecipesCount = (
	cartItems: CartHook["groupItems"],
): {
	recipesCount: number;
	requiredYarns: number;
} => {
	const items = cartItems?.flatMap((item) => item?.items);
	const recipes = items?.filter(
		(item) => ((item.product as RGProduct) || undefined)?.pr_product_type_type === "recipe",
	);

	const recipesCount = recipes
		.map((recipe) => recipe.quantity || 0)
		.reduce((a, b) => (a || 0) + (b || 0), 0);

	const requiredYarns = recipes
		.map((recipe) => +(getRequiredYarns(recipe) ?? YARN_TO_RECIPE_RATIO) * (recipe.quantity || 0))
		.reduce((a, b) => a + b, 0);

	return { recipesCount, requiredYarns };
};

const getYarnsCount = (cartItems: CartHook["groupItems"]): number => {
	const items = cartItems?.flatMap((item) => item?.items).filter(Boolean);
	const yarns = items?.filter(
		(item) => ((item.product as RGProduct) || undefined)?.pr_product_type_type === "yarn",
	);

	const count = yarns.map((yarn) => yarn.quantity || 0).reduce((a, b) => (a || 0) + (b || 0), 0);

	return count;
};

const getBookletsCount = (
	cartItems: CartHook["groupItems"],
): {
	bookletCount: number;
	requiredYarns: number;
} => {
	const items = cartItems?.flatMap((item) => item.items).filter(Boolean);
	const booklets = items?.filter(
		(item) => ((item.product as RGProduct) || undefined)?.pr_product_type_type === "booklet",
	);

	const bookletCount = booklets
		.map((booklet) => booklet.quantity || 0)
		.reduce((a, b) => (a || 0) + (b || 0), 0);

	const requiredYarns = booklets
		.map(
			(booklet) => +(getRequiredYarns(booklet) ?? YARN_TO_RECIPE_RATIO) * (booklet.quantity || 0),
		)
		.reduce((a, b) => a + b, 0);

	return { bookletCount, requiredYarns };
};

const recipesText = (count: number) => {
	if (count === 0) return "";
	const text = count === 1 ? "oppskrift" : "oppskrifter";
	return text;
};

const yarnsText = (count: number) => {
	if (count === 0) return "";
	const text = count === 1 ? "nøste" : "nøster";
	return text;
};

const bookletsText = (count: number) => {
	if (count === 0) return "";
	const text = count === 1 ? "hefte" : "hefter";
	return text;
};

export const missingText = (recipesCount: number, bookletCount: number, missingCount: number) => {
	const recipes = recipesText(recipesCount);
	const booklets = bookletsText(bookletCount);
	const yarns = yarnsText(missingCount);

	// Du må kjøpe [amount] garn til for å kunne kjøpe [amount] oppskrifter og [amount] hefter.

	if (recipesCount === 0) {
		return `Du må kjøpe ${missingCount} garnnøster for å kunne kjøpe ${bookletCount} ${booklets}.`;
	}

	if (bookletCount === 0) {
		return `Du må kjøpe ${missingCount} garnnøster for å kunne kjøpe ${recipesCount} ${recipes}.`;
	}

	const text = `Du må kjøpe ${missingCount} ${yarns} for å kunne kjøpe ${recipesCount} ${recipes} og ${bookletCount} ${booklets}.`;

	return text;
};

export const missingYarnsCount = (items: CartHook["groupItems"]) => {
	let count = 0;

	const { recipesCount, requiredYarns: recipeYarnsRequired } = getRecipesCount(items);
	const yarnsCount = getYarnsCount(items);
	const { bookletCount, requiredYarns: bookletYarnsRequired } = getBookletsCount(items);

	const requiredYarns = recipeYarnsRequired + bookletYarnsRequired;

	count = requiredYarns - yarnsCount;

	const text = missingText(recipesCount, bookletCount, count);

	return {
		missingCount: count,
		text,
		yarnsCount,
		recipesCount,
		bookletCount,
		isMissing: count > 0,
	};
};
